<template>
    <div class="w-full rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800">
        <h3 class="mb-4 flex items-center gap-2 text-base font-semibold text-gray-900 dark:text-white">
            Evolução das Comissões
            <span
                v-if="period"
                class="inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-500 dark:bg-gray-700 dark:text-gray-400"
            >
                Últimos {{ period }} dias
            </span>
        </h3>
        <ticto-loading v-if="fetching" />
        <inline-empty-state
            v-else-if="!hasData"
            :icon="chartIcon"
            :title="chartEmptyTitle"
        />
        <highcharts v-else :key="dark ? 'dark' : 'light'" class="hc" :options="themedChartObject" ref="chart"></highcharts>
    </div>
</template>

<script>
import Highcharts from 'highcharts';
import { PresentationChartLineIcon } from '@heroicons/vue/24/outline';
import { Store } from '../../../stores/store';
import { mapWritableState } from 'pinia';
import TictoLoading from '../../../components/layouts/TictoLoading.vue';
import InlineEmptyState from '../../../components/layouts/InlineEmptyState.vue';

export default {
    components: { TictoLoading, InlineEmptyState },
    props: {
        user: {
            type: Object,
            required: true
        },
        filters: {
            type: Object,
            required: true
        },
        loading: {
            type: Boolean,
            default: false
        },
        period: {
            type: Number,
            default: null
        }
    },
    data() {
        return {
            fetching: false,
            chartObject: {
                tooltip: {
                    formatter: function() {
                        let chart = this;

                        let date = new Date(chart.x);
                        let formattedDate = date.toLocaleDateString("pt-BR", {
                            year: 'numeric',
                            month: '2-digit',
                            day: '2-digit'
                        });

                        return `<b>${formattedDate}</b><br />${chart.points
                            .sort((pointA, pointB) => pointB.y - pointA.y)
                            .map((point) => {
                                return `<span style="color: ${point.color}">\u25CF</span> ${point.series.name}: ` + 
                                    point.y.toLocaleString("pt-br", {
                                        style: "currency",
                                        currency: "BRL",
                                    });
                            })
                            .join("<br />")}`;
                    },
                    xDateFormat: '%d/%m/%Y',
                    shared: true,
                    split: false,
                    enabled: true
                },
                credits: {
                    enabled: false
                },
                legend: {
                    enabled: true
                },
                title: {
                    align: 'left',
                    text: null
                },
                xAxis: {
                    type: 'datetime',
                },
                yAxis: {
                    title: null,
                },
                series: []
            }
        };
    },

    computed: {
        ...mapWritableState(Store, ['dark']),

        hasData() {
            return (this.chartObject.series?.[0]?.data?.length ?? 0) > 0;
        },

        chartIcon() {
            return PresentationChartLineIcon;
        },

        chartEmptyTitle() {
            return this.period
                ? `Sem comissões nos últimos ${this.period} dias`
                : 'Sem comissões no período selecionado';
        },

        // O tema global do Highcharts (setado uma vez em App.vue) é sempre
        // escuro e não acompanha o toggle de tema do app; sobrescrevemos aqui
        // para este gráfico específico seguir o tema claro/escuro corrente.
        themedChartObject() {
            const gridColor = this.dark ? '#374151' : '#e5e7eb';
            const textColor = this.dark ? '#9ca3af' : '#6b7280';
            const lineColor = this.dark ? '#374151' : '#e5e7eb';

            return {
                ...this.chartObject,
                chart: {
                    backgroundColor: 'transparent',
                    style: { fontFamily: 'inherit' },
                },
                xAxis: {
                    ...this.chartObject.xAxis,
                    gridLineColor: gridColor,
                    lineColor,
                    tickColor: lineColor,
                    labels: { style: { color: textColor } },
                },
                yAxis: {
                    ...this.chartObject.yAxis,
                    gridLineColor: gridColor,
                    labels: { style: { color: textColor } },
                },
                legend: {
                    ...this.chartObject.legend,
                    itemStyle: { color: this.dark ? '#e5e7eb' : '#374151' },
                    itemHoverStyle: { color: this.dark ? '#ffffff' : '#111827' },
                },
                tooltip: {
                    ...this.chartObject.tooltip,
                    backgroundColor: this.dark ? 'rgba(17, 24, 39, 0.95)' : 'rgba(255, 255, 255, 0.97)',
                    borderColor: gridColor,
                    style: { color: this.dark ? '#f3f4f6' : '#111827' },
                },
                colors: ['#14b8a6'],
            };
        },
    },

    watch: {
        filters: {
            handler() {
                this.fetch();
            },
            deep: true
        }
    },

    mounted() {
        this.fetch();
    },

    methods: {
        fetch() {
            this.fetching = true;

            axios.get(`/api/user/${this.user.id}/commission/chart`, {
                params: this.filters,
            }).then(({ data }) => {
                this.chartObject.series = [{
                    name: 'Comissões',
                    lineWidth: 3,
                    data: data.map(item => [
                        item.unix_date,
                        item.amount / 100
                    ])
                }];
            }).finally(() => {
                this.fetching = false;
            });
        }
    }
};
</script>
